/**
 * EnterpriseCommandPalette — Sprint A3 (22 Feb 2026).
 *
 * DE primaire navigatie- en werk-interface van het Enterprise Platform.
 * Vervangt GlobalSearchDialog. Volledig geïntegreerd met:
 *   - `EntitySearchService` (POST /api/admin/entity-search) voor entiteiten
 *   - `NavigationRegistry` (GET /api/admin/navigation) voor sectie-navigatie
 *   - `commandRegistry` voor context-aware acties + slash commands
 *   - `user_preferences` voor recente zoekopdrachten / entiteiten / acties
 *   - `EnterpriseEntityPicker` (in feite: dezelfde search hook)
 *
 * ARCHITECTUUR:
 *   Vier hoofdsecties: Navigatie · Entiteiten · Acties · Help
 *
 *   Smart ranking: bij lege input toont "recente selecties" + "quick
 *   actions". Bij typen: exact match > startswith > recente > alfabetisch.
 *
 *   Slash commands: /customer, /invoice, ... routeren de resterende query
 *   naar dezelfde EntitySearchService gefilterd op het gevraagde type.
 *
 *   AI READY: elke action heeft optioneel `ai_intent` — een toekomstige
 *   AI-laag kan natuurlijke-taal-commando's parsen naar een action.key.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import {
  Search, ArrowRight, Clock, Command as CommandIcon,
  Compass, Zap, HelpCircle, Sparkles, Loader2,
  ChevronRight, X, Star,
  // Dynamisch iconen — we resolveren strings → components hieronder
  Users, FileText, Package, FileSignature, Truck, Ship, Anchor,
  Plane, Warehouse, Container as ContainerIcon, UserCog, Ticket, Boxes,
  Bell, CreditCard, CheckCircle2, FileMinus, MessageSquare, Download,
  UserPlus, Receipt, FilePlus, MapPin, PackagePlus, PlaneTakeoff,
  Send, ExternalLink, MoveRight, Printer, Camera, MapPinned,
  Keyboard, BookOpen, LifeBuoy, LayoutDashboard,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../../ui/dialog";
import { adminApi, useAuth } from "../../../context/AuthContext";
import { useUserPreferences } from "../../../context/UserPreferencesContext";
import {
  getContextActions, resolveSlashCommand, getAllSlashRoutes,
} from "./commandRegistry";
import { getEntityConfig } from "../shared/entityRegistry";

const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 6;

// Icon-string → lucide component (voor server-gedreven configs)
const ICON_MAP = {
  Users, FileText, Package, FileSignature, Truck, Ship, Anchor,
  Plane, Warehouse, Container: ContainerIcon, UserCog, Ticket, Boxes,
  Bell, CreditCard, CheckCircle2, FileMinus, MessageSquare, Download,
  UserPlus, Receipt, FilePlus, MapPin, PackagePlus, PlaneTakeoff,
  Send, ExternalLink, MoveRight, Printer, Camera, MapPinned,
  Keyboard, BookOpen, LifeBuoy, LayoutDashboard, Star, Compass, Zap,
  HelpCircle,
};

function Icon({ name, className = "w-4 h-4" }) {
  const Cmp = ICON_MAP[name] || Compass;
  return <Cmp className={className} />;
}

// Detect Mac voor keyboard-hints
const IS_MAC = typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform);
const MOD_KEY_LABEL = IS_MAC ? "⌘" : "Ctrl";

/**
 * Publieke hook — bind CMD+K / CTRL+K keyboard shortcut.
 */
export function useCommandPaletteShortcut(onOpen) {
  useEffect(() => {
    const handler = (e) => {
      const isK = e.key === "k" || e.key === "K";
      const modPressed = e.metaKey || e.ctrlKey;
      if (isK && modPressed) {
        e.preventDefault();
        onOpen();
      } else if (e.key === "/" && !isTypingInInput(e.target)) {
        // "/" openen tenzij user typt in input/textarea
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);
}

function isTypingInInput(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return tag === "input" || tag === "textarea" || el.isContentEditable;
}

// ─────────────────────────────────────────────────────────────
// Section renderer — kop + rijen
// ─────────────────────────────────────────────────────────────
function SectionHeader({ icon, label, count }) {
  return (
    <div className="px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/70 border-t border-slate-100">
      <Icon name={icon} className="w-3 h-3" />
      <span>{label}</span>
      {typeof count === "number" && (
        <span className="ml-1 text-slate-400 font-normal">· {count}</span>
      )}
    </div>
  );
}

function Row({ activeIdx, idx, onSelect, onHover, testId, children }) {
  const isActive = idx === activeIdx;
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      onClick={onSelect}
      onMouseEnter={onHover}
      className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-slate-100 last:border-b-0 transition-colors focus:outline-none ${
        isActive ? "bg-blue-50 border-l-2 border-l-blue-600" : "hover:bg-slate-50"
      }`}
      data-testid={testId}
      data-idx={idx}
    >
      {children}
      {isActive && (
        <ArrowRight className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
      )}
    </button>
  );
}

// Preview-card rijen voor entities — layout per entity_type via config
function EntityRow({ item }) {
  const cfg = getEntityConfig(item.entity_type);
  const IconCmp = cfg.icon;
  const badges = item.badges || [];
  return (
    <>
      {item.avatar_text ? (
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
          {item.avatar_text}
        </div>
      ) : (
        <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
          <IconCmp className="w-4 h-4" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-slate-900 truncate">
            {item.primary_text}
          </span>
          {badges.map((b, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                {
                  success: "bg-emerald-100 text-emerald-800 border-emerald-200",
                  info: "bg-blue-100 text-blue-800 border-blue-200",
                  warning: "bg-amber-100 text-amber-800 border-amber-200",
                  danger: "bg-red-100 text-red-800 border-red-200",
                  neutral: "bg-slate-100 text-slate-700 border-slate-200",
                  muted: "bg-slate-50 text-slate-500 border-slate-200",
                }[b.tone] || "bg-slate-100 text-slate-700 border-slate-200"
              }`}
            >
              {b.label}
            </span>
          ))}
        </div>
        {item.secondary_text && (
          <div className="text-xs text-slate-500 truncate">{item.secondary_text}</div>
        )}
      </div>
      <span className="text-[10px] uppercase font-mono text-slate-400 mr-1">
        {cfg.label}
      </span>
    </>
  );
}

// Navigation row
function NavRow({ item, group }) {
  return (
    <>
      <div className="w-8 h-8 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
        <Icon name={item.icon || "Compass"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">
            {group?.label}
          </span>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className="font-medium text-sm text-slate-900 truncate">
            {item.label}
          </span>
        </div>
        {item.description && (
          <div className="text-xs text-slate-500 truncate">{item.description}</div>
        )}
      </div>
    </>
  );
}

// Action row
function ActionRow({ action, isFavorite, onToggleFavorite }) {
  return (
    <>
      <div className="w-8 h-8 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
        <Icon name={action.icon} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm text-slate-900 truncate">
          {action.label}
        </span>
        {action.description && (
          <div className="text-xs text-slate-500 truncate">{action.description}</div>
        )}
      </div>
      {action.shortcut && (
        <div className="flex gap-0.5 mr-2">
          {action.shortcut.map((k, i) => (
            <kbd
              key={i}
              className="px-1.5 py-0.5 text-[10px] bg-slate-100 border border-slate-200 rounded font-mono"
            >
              {k === "mod" ? MOD_KEY_LABEL : k === "shift" ? "⇧" : k.toUpperCase()}
            </kbd>
          ))}
        </div>
      )}
      {onToggleFavorite && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(action.key); }}
          className={`p-1 rounded transition-colors ${
            isFavorite ? "text-amber-500 hover:text-amber-600" : "text-slate-300 hover:text-amber-400"
          }`}
          aria-label={isFavorite ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
          data-testid={`command-palette-fav-${action.key}`}
        >
          <Star className={`w-3.5 h-3.5 ${isFavorite ? "fill-current" : ""}`} />
        </button>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Smart ranking — vanuit user_preferences (recent) en query-string
// ─────────────────────────────────────────────────────────────
function rankRows(rows, query) {
  if (!query) return rows;
  const q = query.toLowerCase();
  const score = (row) => {
    const label = (row._searchText || row.label || row.primary_text || "").toLowerCase();
    if (label === q) return 100;
    if (label.startsWith(q)) return 80;
    if (label.includes(q)) return 50;
    return 0;
  };
  return rows
    .map((r) => ({ ...r, _score: score(r) }))
    .filter((r) => r._score > 0)
    .sort((a, b) => b._score - a._score);
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function EnterpriseCommandPalette({
  open,
  onOpenChange,
  activeSection = null,       // huidige nav-key ("klanten", "facturen", ...)
  activeEntityType = null,    // "customer" | "invoice" | "booking" | ...
  activeEntityId = null,      // wanneer een entity-detail open is
  navigation = null,          // { groups: [...], quick_actions: [...] } from useNavigationRegistry
  onNavigate,                 // (key) => void — navigatie handler
}) {
  const { hasPermission } = useAuth();
  const { isCommandFavorite, toggleCommandFavorite, favoriteCommands } = useUserPreferences();
  const [query, setQuery] = useState("");
  const [entityResults, setEntityResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentEntities, setRecentEntities] = useState([]);

  const trimmedQuery = query.trim();
  const slashRoute = useMemo(() => resolveSlashCommand(trimmedQuery), [trimmedQuery]);

  // Available entity types from the registry (permission-filtered).
  // Bij slash-command: alleen dat type. Anders: alle standaard types.
  const availableEntityTypes = useMemo(() => {
    if (slashRoute?.entityType) return [slashRoute.entityType];
    // Default set — kernstukken van 5999Cargo
    return ["customer", "invoice", "booking", "quote", "shipment"];
  }, [slashRoute]);

  const effectiveQuery = slashRoute ? slashRoute.remainingQuery : trimmedQuery;

  // ─── Fetch recent searches/entities bij openen ─────────────
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const prefs = await adminApi.get("/admin/user-preferences");
        setRecentSearches((prefs.data?.recent_searches || []).slice(0, 5));
        // Flatten recent entities from all entity-types
        const recentByType = prefs.data?.recent_entity_selections || {};
        const merged = [];
        Object.values(recentByType).forEach((bucket) =>
          bucket.slice(0, 3).forEach((r) => merged.push(r))
        );
        merged.sort((a, b) => (b.selected_at || "").localeCompare(a.selected_at || ""));
        setRecentEntities(merged.slice(0, 6));
      } catch {
        /* silent */
      }
    })();
  }, [open]);

  // ─── Debounced entity search ───────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (!effectiveQuery && !slashRoute) {
      setEntityResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const body = {
          entity_types: availableEntityTypes,
          q: effectiveQuery || "",
          limit: RESULT_LIMIT,
        };
        const r = await adminApi.post("/admin/entity-search", body);
        const flat = [];
        availableEntityTypes.forEach((et) => {
          (r.data?.results?.[et] || []).forEach((it) => flat.push(it));
        });
        setEntityResults(flat);
      } catch {
        setEntityResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, effectiveQuery, slashRoute, JSON.stringify(availableEntityTypes)]);

  // ─── Context-aware actions ─────────────────────────────────
  const contextActions = useMemo(() => {
    if (slashRoute) return []; // slash-cmd modus toont geen acties
    const actions = getContextActions({
      activeSection,
      entityType: activeEntityType,
      hasPermission,
    });
    const ranked = rankRows(
      actions.map((a) => ({ ...a, _searchText: a.label + " " + (a.description || "") })),
      trimmedQuery
    );
    // Sprint Fase A Afronding — plaats gefavoriete commands bovenaan
    const favSet = new Set(favoriteCommands());
    return ranked.sort((a, b) => {
      const aF = favSet.has(a.key) ? 1 : 0;
      const bF = favSet.has(b.key) ? 1 : 0;
      return bF - aF;
    });
  }, [trimmedQuery, activeSection, activeEntityType, hasPermission, slashRoute, favoriteCommands]);

  // Help section
  const helpActions = useMemo(
    () => contextActions.filter((a) => a.section === "__help__"),
    [contextActions]
  );
  const nonHelpActions = useMemo(
    () => contextActions.filter((a) => a.section !== "__help__"),
    [contextActions]
  );

  // ─── Navigation matches ────────────────────────────────────
  const navRows = useMemo(() => {
    if (slashRoute) return [];
    const items = [];
    (navigation?.groups || []).forEach((g) => {
      (g.items || []).forEach((it) => {
        items.push({
          ...it,
          group_label: g.label,
          _group_ref: g,
          _searchText: `${g.label} ${it.label} ${it.description || ""}`,
        });
      });
    });
    return rankRows(items, trimmedQuery).slice(0, 6);
  }, [navigation, trimmedQuery, slashRoute]);

  // ─── Build flat display-list voor keyboard nav ─────────────
  // Elke row krijgt een unieke _rowKey zodat React-lists nooit dubbele keys
  // krijgen ook al bevat de recent_entity-bucket toevallig dezelfde
  // entity_id als een live entity-result (bijv gebruiker zoekt op een klant
  // die net geopend is geweest).
  const flatRows = useMemo(() => {
    const rows = [];
    if (!trimmedQuery && !slashRoute) {
      recentEntities.forEach((r, i) =>
        rows.push({ _kind: "recent_entity", _rowKey: `recent-${r.entity_type}-${r.entity_id}-${i}`, data: r })
      );
      nonHelpActions.slice(0, 6).forEach((a) =>
        rows.push({ _kind: "action", _rowKey: `action-${a.key}`, data: a })
      );
    } else {
      navRows.forEach((n) => rows.push({ _kind: "nav", _rowKey: `nav-${n.key}`, data: n }));
      entityResults.forEach((e, i) =>
        rows.push({ _kind: "entity", _rowKey: `ent-${e.entity_type}-${e.id}-${i}`, data: e })
      );
      nonHelpActions.forEach((a) =>
        rows.push({ _kind: "action", _rowKey: `action-${a.key}`, data: a })
      );
    }
    helpActions.forEach((h) => rows.push({ _kind: "action", _rowKey: `help-${h.key}`, data: h }));
    return rows;
  }, [trimmedQuery, slashRoute, recentEntities, nonHelpActions, helpActions, navRows, entityResults]);

  // ─── Reset active-idx bij nieuwe rows ──────────────────────
  useEffect(() => {
    setActiveIdx(flatRows.length > 0 ? 0 : -1);
  }, [flatRows.length]);

  // ─── Scroll active-row into view ───────────────────────────
  useEffect(() => {
    if (activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIdx]);

  // ─── Persist search/entity on select ───────────────────────
  const persistSearch = useCallback(async (q, types) => {
    if (!q || q.length < 2) return;
    try {
      await adminApi.post("/admin/user-preferences/recent-searches/add", {
        search: { q, entity_types: types },
      });
    } catch { /* silent */ }
  }, []);

  const persistEntitySelection = useCallback(async (item) => {
    try {
      await adminApi.post("/admin/user-preferences/recent-entity-selections/add", {
        selection: {
          entity_type: item.entity_type,
          entity_id: item.id || item.entity_id,
          primary_text: item.primary_text,
          secondary_text: item.secondary_text || null,
          icon: item.icon || null,
          avatar_text: item.avatar_text || null,
        },
      });
    } catch { /* silent */ }
  }, []);

  // ─── Select handler ────────────────────────────────────────
  const handleSelect = useCallback(
    async (row) => {
      if (!row) return;
      switch (row._kind) {
        case "nav": {
          if (onNavigate) onNavigate(row.data.key);
          onOpenChange(false);
          break;
        }
        case "entity": {
          await persistEntitySelection(row.data);
          persistSearch(trimmedQuery, availableEntityTypes);
          if (row.data.url) {
            window.location.assign(row.data.url);
          }
          onOpenChange(false);
          break;
        }
        case "recent_entity": {
          // Fallback URL alleen als de originele backend-URL ontbreekt.
          // We proberen éérst de section-key uit entityRegistry.section
          // (bv customer → "klanten") — als die niet bestaat vallen we
          // terug op de type-string; wanneer die ook geen ADMIN_MENU-key
          // is, blokkeert AdminPage.jsx de render en zien we "Geen toegang".
          // Beter dan een deadlock: hydrate via entity-search-endpoint.
          // P0 UX Fix (Aug 2026): shipment → warehouse ipv tracking_labels,
          // zodat de VOLLEDIGE ShipmentDetail (7 tabs) direct opent.
          const cfgFallback = getEntityConfig(row.data.entity_type);
          const sectionKey = ({
            customer: "klanten", invoice: "facturen", booking: "documenten",
            quote: "offertes", shipment: "warehouse",
            warehouse_item: "warehouse",
          })[row.data.entity_type] || row.data.entity_type;
          const url = cfgFallback.url_pattern
            ? cfgFallback.url_pattern.replace("{id}", row.data.entity_id)
            : `/admin?section=${sectionKey}&${row.data.entity_type}_id=${row.data.entity_id}`;
          window.location.assign(url);
          onOpenChange(false);
          break;
        }
        case "action": {
          try {
            row.data.action({
              activeSection,
              entityId: activeEntityId,
              entityType: activeEntityType,
              navigate: (k) => onNavigate && onNavigate(k),
            });
          } catch (e) {
            // Silent — action falen mag command palette niet crashen
            void e;
          }
          onOpenChange(false);
          break;
        }
      }
    },
    [trimmedQuery, availableEntityTypes, onOpenChange, onNavigate,
     activeSection, activeEntityId, activeEntityType,
     persistEntitySelection, persistSearch]
  );

  // ─── Keyboard handler ──────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, flatRows.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIdx >= 0 && flatRows[activeIdx]) {
          handleSelect(flatRows[activeIdx]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onOpenChange(false);
      }
    },
    [flatRows, activeIdx, handleSelect, onOpenChange]
  );

  // ─── Reset query bij openen ────────────────────────────────
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ─── Sections + counts voor de UI ──────────────────────────
  const sections = [];
  if (!trimmedQuery && !slashRoute && recentEntities.length > 0) {
    sections.push({
      key: "recent", icon: "Clock", label: "Recent geopend",
      count: recentEntities.length,
    });
  }
  if (navRows.length > 0) {
    sections.push({ key: "nav", icon: "Compass", label: "Navigatie", count: navRows.length });
  }
  if (entityResults.length > 0) {
    sections.push({
      key: "entities", icon: "Boxes",
      label: slashRoute ? `${slashRoute.label} (slash-command)` : "Entiteiten",
      count: entityResults.length,
    });
  }
  if (nonHelpActions.length > 0) {
    sections.push({ key: "actions", icon: "Zap", label: "Acties", count: nonHelpActions.length });
  }
  if (helpActions.length > 0) {
    sections.push({ key: "help", icon: "HelpCircle", label: "Help", count: helpActions.length });
  }

  // ─── Render individual rows in the right order ─────────────
  let cursor = 0;
  function nextIdx() { return cursor++; }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw] p-0 overflow-hidden rounded-xl shadow-2xl border-slate-200 gap-0"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Enterprise Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Zoek en werk binnen het 5999Cargo Enterprise Platform.
        </DialogDescription>

        {/* Search input */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Zoek klanten, facturen, boekingen — of typ /customer, /invoice, …"
            className="flex-1 text-base bg-transparent outline-none placeholder:text-slate-400 text-slate-900"
            data-testid="command-palette-input"
            autoComplete="off"
            aria-controls="command-palette-list"
            aria-activedescendant={activeIdx >= 0 ? `cmd-row-${activeIdx}` : undefined}
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" data-testid="command-palette-loading" />}
          {slashRoute && (
            <span className="text-[10px] uppercase font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded"
                  data-testid="command-palette-slash-indicator">
              /{trimmedQuery.split(/\s+/)[0].replace(/^\//, "")}
            </span>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Empty query hint */}
        {!trimmedQuery && !slashRoute && recentEntities.length === 0 && nonHelpActions.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-slate-400">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            Typ om te zoeken — of gebruik <kbd className="px-1 py-0.5 border border-slate-200 rounded bg-slate-50 font-mono text-[11px]">/customer</kbd> voor slash-commands.
          </div>
        )}

        {/* Result body */}
        <div
          ref={listRef}
          id="command-palette-list"
          role="listbox"
          data-testid="command-palette-list"
          className="max-h-[60vh] overflow-y-auto"
        >
          {/* Recent (bij lege query) */}
          {!trimmedQuery && !slashRoute && recentEntities.length > 0 && (
            <>
              <SectionHeader icon="Clock" label="Recent geopend" count={recentEntities.length} />
              {recentEntities.map((r) => {
                const idx = nextIdx();
                return (
                  <Row
                    key={`recent-${idx}`}
                    activeIdx={activeIdx} idx={idx}
                    onSelect={() => handleSelect({ _kind: "recent_entity", data: r })}
                    onHover={() => setActiveIdx(idx)}
                    testId={`command-palette-recent-${r.entity_id}`}
                  >
                    <EntityRow item={{
                      id: r.entity_id, entity_type: r.entity_type,
                      primary_text: r.primary_text, secondary_text: r.secondary_text,
                      icon: r.icon, avatar_text: r.avatar_text, badges: [],
                    }} />
                  </Row>
                );
              })}
            </>
          )}

          {/* Navigation matches */}
          {navRows.length > 0 && (
            <>
              <SectionHeader icon="Compass" label="Navigatie" count={navRows.length} />
              {navRows.map((n) => {
                const idx = nextIdx();
                return (
                  <Row
                    key={`nav-${n.key}`}
                    activeIdx={activeIdx} idx={idx}
                    onSelect={() => handleSelect({ _kind: "nav", data: n })}
                    onHover={() => setActiveIdx(idx)}
                    testId={`command-palette-nav-${n.key}`}
                  >
                    <NavRow item={n} group={n._group_ref} />
                  </Row>
                );
              })}
            </>
          )}

          {/* Entity results */}
          {entityResults.length > 0 && (
            <>
              <SectionHeader
                icon="Boxes"
                label={slashRoute ? `${slashRoute.label} (slash-command)` : "Entiteiten"}
                count={entityResults.length}
              />
              {entityResults.map((e) => {
                const idx = nextIdx();
                return (
                  <Row
                    key={`ent-${e.entity_type}-${e.id}`}
                    activeIdx={activeIdx} idx={idx}
                    onSelect={() => handleSelect({ _kind: "entity", data: e })}
                    onHover={() => setActiveIdx(idx)}
                    testId={`command-palette-entity-${e.id}`}
                  >
                    <EntityRow item={e} />
                  </Row>
                );
              })}
            </>
          )}

          {/* Context actions */}
          {nonHelpActions.length > 0 && (
            <>
              <SectionHeader icon="Zap" label="Acties" count={nonHelpActions.length} />
              {nonHelpActions.map((a) => {
                const idx = nextIdx();
                return (
                  <Row
                    key={`act-${a.key}`}
                    activeIdx={activeIdx} idx={idx}
                    onSelect={() => handleSelect({ _kind: "action", data: a })}
                    onHover={() => setActiveIdx(idx)}
                    testId={`command-palette-action-${a.key}`}
                  >
                    <ActionRow
                      action={a}
                      isFavorite={isCommandFavorite(a.key)}
                      onToggleFavorite={toggleCommandFavorite}
                    />
                  </Row>
                );
              })}
            </>
          )}

          {/* Help actions */}
          {helpActions.length > 0 && (
            <>
              <SectionHeader icon="HelpCircle" label="Help" count={helpActions.length} />
              {helpActions.map((a) => {
                const idx = nextIdx();
                return (
                  <Row
                    key={`help-${a.key}`}
                    activeIdx={activeIdx} idx={idx}
                    onSelect={() => handleSelect({ _kind: "action", data: a })}
                    onHover={() => setActiveIdx(idx)}
                    testId={`command-palette-help-${a.key}`}
                  >
                    <ActionRow action={a} />
                  </Row>
                );
              })}
            </>
          )}

          {/* No results state */}
          {trimmedQuery && flatRows.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-slate-500"
                 data-testid="command-palette-empty">
              Geen resultaten voor &quot;{trimmedQuery}&quot;.
              <div className="mt-2 text-xs text-slate-400">
                Tip: gebruik slash-commands zoals /customer of /invoice.
              </div>
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">↑↓</kbd> navigeer
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">Enter</kbd> selecteer
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">Esc</kbd> sluit
            </span>
          </span>
          <span className="text-slate-400">
            {flatRows.length} {flatRows.length === 1 ? "resultaat" : "resultaten"}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
